import { useNavigate } from 'react-router-dom';
import { useWeekEvents } from '../hooks/useEventData';
import { useWeekAvailability, useWeeklyPlan, usePlanFeedback } from '../hooks/useAvailability';
import { useAuth } from '../context/AuthContext';
import { formatTime, getWeekDates, isToday } from '../utils/helpers';
import EventBadge from '../components/EventBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import AvailabilityToggle from '../components/AvailabilityToggle';
import AvailabilityBanner from '../components/AvailabilityBanner';
import AdminAvailabilitySummary from '../components/AdminAvailabilitySummary';
import WeeklyPlanPanel from '../components/WeeklyPlanPanel';
import PlanFeedbackPanel from '../components/PlanFeedbackPanel';
import { useGroupData } from '../hooks/useGroupData';
import type { CalendarEvent, AvailabilityStatus } from '../types';

export default function WeeklyViewA() {
  const { events, loading, error, refresh } = useWeekEvents();
  const { user } = useAuth();
  const navigate = useNavigate();
  const weekDates = getWeekDates();

  const activeEvents = events.filter(e => e.status === 'on');
  const { availability, setMyAvailability } = useWeekAvailability(activeEvents);
  const { plan, generating, generate, share, publish, editAssignment } = useWeeklyPlan();
  const { feedback, summary: feedbackSummary, submitting: feedbackSubmitting, submit: submitFeedback } = usePlanFeedback(plan?.id || null);
  const myFeedback = feedback.find(f => f.userId === user?.id) || null;

  const eventsByDate = new Map<string, CalendarEvent>();
  events.forEach(e => eventsByDate.set(e.date, e));

  const isAdmin = user?.role === 'admin';
  const { members } = useGroupData();

  // Count events where current user hasn't set availability
  const missingCount = activeEvents.filter(e => {
    const records = availability[e.id] || [];
    return !records.some(r => r.userId === user?.id);
  }).length;

  const handleAvailability = async (eventId: string, status: AvailabilityStatus) => {
    await setMyAvailability(eventId, status);
  };

  if (loading) return <LoadingSpinner className="mt-20" />;
  if (error) return <div className="p-6 text-center text-cancelled">{error}</div>;

  return (
    <div className="px-5 pt-8 pb-6 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">This Week</h1>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
        {new Date(weekDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
        {' - '}
        {new Date(weekDates[6] + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>

      <div className="mt-4">
        <AvailabilityBanner missingCount={missingCount} />
        {isAdmin && <WeeklyPlanPanel plan={plan} generating={generating} onGenerate={generate} onShare={share} onPublish={publish} feedback={feedback} feedbackSummary={feedbackSummary} members={members} onEditAssignment={editAssignment} />}
        {!isAdmin && plan && plan.status === 'pending_review' && (
          <PlanFeedbackPanel plan={plan} myFeedback={myFeedback} submitting={feedbackSubmitting} onSubmit={submitFeedback} />
        )}
      </div>

      <div className="mt-2 space-y-3">
        {weekDates.map(date => {
          const d = new Date(date + 'T00:00:00');
          const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
          const dayNum = d.getDate();
          const event = eventsByDate.get(date);
          const today = isToday(date);

          const myAvail = event && event.status === 'on'
            ? (availability[event.id] || []).find(r => r.userId === user?.id)?.status || null
            : null;

          return (
            <div key={date} className={`rounded-2xl p-4 transition-all ${
              event
                ? 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm'
                : 'bg-gray-50/50 dark:bg-gray-900/30'
            } ${today ? 'ring-2 ring-practice/30 dark:ring-practice/20' : ''}`}>
              <button
                onClick={() => event && navigate(`/event/${event.id}`)}
                disabled={!event}
                className="w-full text-left min-h-tap"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 text-center">
                    <p className={`text-xs font-semibold uppercase tracking-wide ${
                      today ? 'text-practice dark:text-practice-light' : 'text-gray-400 dark:text-gray-500'
                    }`}>{dayName}</p>
                    <p className={`text-2xl font-bold ${
                      today ? 'text-practice dark:text-practice-light' : event ? 'text-gray-900 dark:text-white' : 'text-gray-300 dark:text-gray-700'
                    }`}>{dayNum}</p>
                  </div>

                  {event ? (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <EventBadge type={event.type} status={event.status} size="sm" />
                        {event.title && (
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{event.title}</span>
                        )}
                      </div>
                      {event.status !== 'cancelled' && (
                        <>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                          </p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 truncate">{event.locationName}</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 py-1">
                      <p className="text-sm text-gray-300 dark:text-gray-700">No event</p>
                    </div>
                  )}

                  {event && event.status !== 'cancelled' && (
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>

              {/* Availability toggle */}
              {event && event.status === 'on' && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 dark:text-gray-500">Driving</span>
                    <AvailabilityToggle status={myAvail} onChange={(s) => handleAvailability(event.id, s)} />
                  </div>
                  {isAdmin && (
                    <AdminAvailabilitySummary members={members} records={availability[event.id] || []} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
